import { useFlags } from "launchdarkly-react-client-sdk";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import {
  AvailableIntegrationFragment,
  IntegrationAbility,
  TransformTool,
} from "@/apollo/types";
import { useLatestValueRef } from "@/hooks/useLatestValueRef";
import {
  hasAbility,
  useIntegration,
  useIntegrationsAreStale,
} from "@/integrations";

import { useCurrentAccount } from "./account";

const DataWarehouseContext = createContext<{
  integration: AvailableIntegrationFragment;
  connectionId: string;
  features: {
    isMaterializedTableEnabled: boolean;
    isTransformEnabled: boolean;
    isExtractEnabled: boolean;
    isActivateEnabled: boolean;
  };
} | null>(null);

export function useDataWarehouseContext() {
  const context = useContext(DataWarehouseContext);
  if (!context) {
    throw new Error(
      "useDataWarehouseContext must be used within a DataWarehouseProvider",
    );
  }
  return context;
}

export function DataWarehouseProvider(props: {
  children: React.ReactNode;
  onIntegrationNotFound?: (
    account: ReturnType<typeof useCurrentAccount>,
  ) => void;
}) {
  const account = useCurrentAccount();

  const integration = useIntegration(
    account.dataWarehouse?.integrationId ?? "",
  );

  const { enableWeldTransformAlongsideExternal } = useFlags();

  const connectionId = account.dataWarehouse?.connectionId ?? "";

  const features = useMemo(() => {
    const isTransformEnabled =
      account.accountSettings.transformTool === TransformTool.Weld ||
      account.accountSettings.transformTool === TransformTool.Github ||
      account.accountSettings.transformTool === null ||
      enableWeldTransformAlongsideExternal;

    if (!integration) {
      return {
        isMaterializedTableEnabled: false,
        isTransformEnabled: false,
        isExtractEnabled: false,
        isActivateEnabled: false,
      };
    }
    return {
      // All the DataWarehouse integrations must support materialized tables (either built-in, or via Weld table creation)
      isMaterializedTableEnabled: hasAbility(
        integration,
        IntegrationAbility.DataWarehouse,
      ),
      isTransformEnabled:
        isTransformEnabled &&
        hasAbility(integration, IntegrationAbility.DataWarehouse),
      isExtractEnabled: hasAbility(
        integration,
        IntegrationAbility.EltDestination,
      ),
      isActivateEnabled: hasAbility(
        integration,
        IntegrationAbility.ActivateSource,
      ),
    };
  }, [
    integration,
    account.accountSettings,
    enableWeldTransformAlongsideExternal,
  ]);

  // Held back while the catalogue is a persisted snapshot: reporting the
  // integration missing there — and redirecting away — would act on a previous
  // load's data that the in-flight response is about to replace. Fires at most
  // once, as the mount effect it replaces did.
  const integrationsAreStale = useIntegrationsAreStale();
  const reportedNotFound = useRef(false);
  const onIntegrationNotFoundRef = useLatestValueRef(
    props.onIntegrationNotFound,
  );
  useEffect(() => {
    if (integrationsAreStale || integration || reportedNotFound.current) return;
    reportedNotFound.current = true;
    onIntegrationNotFoundRef.current?.(account);
  }, [integrationsAreStale, integration, account, onIntegrationNotFoundRef]);

  if (!integration) {
    // eslint-disable-next-line no-console
    console.warn(
      "DataWarehouseProvider: Integration not found",
      account.dataWarehouse?.integrationId,
    );
    return null;
  }

  return (
    <DataWarehouseContext.Provider
      value={{ integration, features, connectionId }}
    >
      {props.children}
    </DataWarehouseContext.Provider>
  );
}
